/**
 * @fileoverview ACTIVE ACCELERATOR COMPONENT FOR COMPANIES CARD VIEW
 * 
 * THIS IS THE CURRENT ACTIVE IMPLEMENTATION FOR DISPLAYING ACCELERATOR INFORMATION IN COMPANY CARDS.
 * It displays the accelerator logo (Y Combinator, Techstars, etc.) and cohort information for a company
 * in the card view of the /companies page.
 * 
 * @active CONFIRMED - This component is actively used in the application
 * 
 * @see {@link src/components/companies/newCompaniesCards/companyNewCardItem/index.tsx} - Parent component that uses this in card view
 * @see {@link src/components/ui/buttons/acceleratorSelector.tsx} - Utility for selecting accelerator icons (Y Combinator, Techstars, etc.)
 * @see {@link src/components/companies/companiesList/newCompaniesList/companiesTable/shared/AcceleratorListView.tsx} - Version used in table view
 * @see {@link src/components/companies/companiesList/newCompaniesList/companiesCards/companyCardItem/AcceleratorDetailPage.tsx} - Version used in company detail pages
 */

import { type ReactNode } from "react";

import { Flex, Text, type FlexProps } from "@chakra-ui/react";

import { acceleratorSelector } from "@/components/ui/buttons/acceleratorSelector";

/**
 * Company data interface with accelerator information
 */
interface CompanyWithAccelerator {
  id?: string;
  initialData?: {
    accelerator?: Array<{ id: string; slug: string }> | { id: string; slug: string; incubator_name?: string };
    [key: string]: any;
  };
  acceleratorBatch?: {
    cohort?: string;
    [key: string]: any;
  };
  [key: string]: any;
}

/**
 * Props for the AcceleratorCardView component
 */
interface AcceleratorCardViewProps {
  /**
   * Company data containing accelerator information
   */
  item: CompanyWithAccelerator;
  
  /**
   * Optional custom styles for the container
   */
  containerStyle?: FlexProps;
  
  /**
   * Optional custom styles for the text
   */
  textStyle?: Record<string, any>;
  
  /**
   * Optional children to render after the accelerator
   */
  children?: ReactNode;
}

/**
 * Active accelerator component that displays accelerator information in company cards
 * Used in the card view of the /companies page
 * 
 * @param {AcceleratorCardViewProps} props - Component props
 * @returns {JSX.Element|null} Rendered component or null if no accelerator data
 */
export const AcceleratorCardView = ({ 
  item,
  containerStyle = {},
  textStyle = {},
  children
}: AcceleratorCardViewProps) => {
  // Return null if no accelerator data
  // Check both initialData.accelerator and accelerator properties
  if (!item?.initialData?.accelerator && !item?.accelerator) {
    return null;
  }
  
  // Debug the incoming data structure
  console.log('AcceleratorCardView received item:', {
    itemId: item?.id,
    hasInitialData: Boolean(item?.initialData),
    accelerator: item?.accelerator,
    acceleratorInInitialData: item?.initialData?.accelerator,
    acceleratorType: item?.accelerator ? (Array.isArray(item.accelerator) ? 'array' : typeof item.accelerator) : 'undefined',
    keys: item?.accelerator ? Object.keys(item.accelerator) : [],
    acceleratorBatch: item?.acceleratorBatch
  });

  // Prepare the company data in the format expected by acceleratorSelector
  const companyData = {
    initialData: {
      accelerator: item?.initialData?.accelerator || item?.accelerator,
      webUrl: item?.initialData?.webUrl
    }
  };

  // Get the accelerator icon with debug logging
  console.log('Calling acceleratorSelector with:', companyData);
  // Use type assertion to fix TypeScript error
  const acceleratorIcon = acceleratorSelector({ company: companyData as any });
  console.log('acceleratorSelector returned:', acceleratorIcon ? 'Icon component' : 'null');
  
  // Return null if no accelerator icon
  if (!acceleratorIcon) {
    return null;
  }

  return (
    <Flex
      w={"fit-content"}
      align={"center"}
      columnGap={"8px"}
      bg={"white"}
      filter={"drop-shadow(0 0 0.5rem rgba(128, 128, 128, 0.25))"}
      borderRadius={"4px"}
      px={"12px"}
      py={"6px"}
      cursor={"pointer"}
      {...containerStyle}
    >
      {acceleratorIcon}

      {item?.acceleratorBatch?.cohort && (
        <Text
          fontSize={"14px"}
          fontWeight={"700"}
          lineHeight={"1"}
          letterSpacing={"2%"}
          mt={"2px"}
          {...textStyle}
        >
          {item.acceleratorBatch.cohort}
        </Text>
      )}
      
      {children}
    </Flex>
  );
};
